import React from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

import { ConsumptionApp, OmaLummeApp } from '../../omaLummeContext';

// Actions.
import { fetchContractBasicData, fetchContractDetails, fetchInvoices, fetchLedgers } from '../../actions';
// Types.
import { InvoiceAkamon, InvoiceAkamonPaginated, SortedContracts } from '../../types/billing';
import { WrapperProps } from '../../types/context';
import { Contract } from '../../types/contract';
// Utils
import { filterOutUndefinedContracts, filterOutContractsWithSameStartAndEndAndStateS, alphabeticallySortContracts } from '../../utils/contracts';

export const contractBatchAmount = 25;

/**
 * Contracts page. Displays a list of contracts.
 */
const ContractsWrapper: React.FunctionComponent<WrapperProps> = (props: WrapperProps) => {
  const { t } = useTranslation();
  const dispatch = OmaLummeApp.useDispatch();
  const dispatchConsumption = ConsumptionApp.useDispatch();
  const { users, basicData, loadingBasicData, contracts, invoices, selectedMeteringPoint } = OmaLummeApp.useState();
  const { children } = props;

  const [fetchingInvoices, setFetchingInvoices] = React.useState<boolean>(false);
  const [allContracts, setAllContracts] = React.useState<SortedContracts[]>([]);
  const [contractsFetched, setContractsFetched] = React.useState(false);
  const [additionalInvoices, setAdditionalInvoices] = React.useState<InvoiceAkamon[]>([]);
  const [userHasNoContracts, setUserHasNoContracts] = React.useState<boolean | number>(false);
  const [contractIndex, setContractIndex] = React.useState<number>(1);
  const [prevUser, setPrevUser] = React.useState<number>(users.activeUser.userId);
  const currentActiveUser = users.activeUser;
  const endpoint = currentActiveUser.endpoint;

  React.useEffect(() => {
    if (currentActiveUser.userId !== prevUser) {
      setPrevUser(currentActiveUser.userId);
      setContractsFetched(false);
      setAllContracts([]);
      dispatch(['changeBasicData', undefined]);
      dispatch(['changeContracts', undefined]);
      dispatch(['changeLedgers', undefined]);
      dispatch(['changeInvoices', undefined]);
      dispatch(['changeSelectedMeteringPoint', undefined]);
      setAdditionalInvoices([]);
      setContractIndex(1);
    }
  }, [currentActiveUser]);

  React.useEffect(() => {
    const userHasContracts = (updatedIndex: number, filteredFetchedContracts: Contract[]) => {
      setUserHasNoContracts(false);
      setContractIndex(updatedIndex);

      const sortedContracts = filteredFetchedContracts.sort((a, b) => alphabeticallySortContracts(a, b));

      return sortedContracts;
    };
    // Create an scoped async function in the hook
    async function fetchUserContracts(): Promise<void> {
      // If we shouldn't fetch contracts, there are no contracts found for the current user or the current userid is 0 (a fake user)
      // We don't need to do anything and just return.
      if (userHasNoContracts === currentActiveUser.userId || (userHasNoContracts && currentActiveUser.userId === 0)) {
        if (currentActiveUser.userId === 0) {
          setUserHasNoContracts(true);
          setContractsFetched(true);
        }
        return;
      }

      let userFoundInContracts = false;
      // We check our stored contracts and see if we need to fetch the current users contracts again.
      for (const contractGroup of allContracts) {
        if (contractGroup.userId === currentActiveUser.userId && contractGroup.endPoint === currentActiveUser.endpoint) {
          userFoundInContracts = true;
          if ((contractIndex - 1) % contractBatchAmount !== 0) {
            break;
          }
        }
      }

      // Check if the user has access to contracts owned by other users
      if ((!userFoundInContracts && currentActiveUser.userId !== 0) || (contractIndex - 1) % contractBatchAmount === 0) {
        if (contractIndex == 1 || (contractIndex - 1) % contractBatchAmount == 0) {
          setContractsFetched(false);
          const fetchedContracts = await fetchContractDetails(currentActiveUser.userId, currentActiveUser.userId, endpoint, contractIndex, 0);
          let filteredFetchedContracts = fetchedContracts.filter(filterOutUndefinedContracts).filter(filterOutContractsWithSameStartAndEndAndStateS);

          let sortedContracts;
          const updatedIndex = contractIndex + fetchedContracts.length;

          if (updatedIndex === 0 || filteredFetchedContracts.length === 0) {
            let workingIndex = contractIndex;
            if (fetchedContracts.length === contractBatchAmount) {
              filteredFetchedContracts = [];
              while (filteredFetchedContracts.length === 0) {
                const fetchedContracts = await fetchContractDetails(currentActiveUser.userId, currentActiveUser.userId, endpoint, workingIndex, 0);
                filteredFetchedContracts = fetchedContracts
                  .filter(filterOutUndefinedContracts)
                  .filter(filterOutContractsWithSameStartAndEndAndStateS);
                if (filteredFetchedContracts.length > 0 || fetchedContracts.length < contractBatchAmount) {
                  break;
                }
                workingIndex += contractBatchAmount;
              }
              setContractIndex(workingIndex);
            }

            if (workingIndex <= 1) {
              // Fake users have a userId of 0.
              // Setting the user to have an id of 0 counts as false so we explicitly set it to true.
              let userWithNoContracts: number | boolean = currentActiveUser.userId;
              if (userWithNoContracts === 0) {
                userWithNoContracts = true;
              }
              setUserHasNoContracts(userWithNoContracts);
              setContractsFetched(true);
              return;
            } else {
              sortedContracts = userHasContracts(updatedIndex, filteredFetchedContracts);
            }
          } else {
            sortedContracts = userHasContracts(updatedIndex, filteredFetchedContracts);
          }
          if (userFoundInContracts) {
            for (const contractGroup of allContracts) {
              if (contractGroup.userId === currentActiveUser.userId && contractGroup.endPoint === currentActiveUser.endpoint) {
                const concatContracts = new Set(contractGroup.contracts.map((contract) => contract.contractNo));
                contractGroup.contracts = [
                  ...contractGroup.contracts,
                  ...sortedContracts.filter((contract) => !concatContracts.has(contract.contractNo)),
                ];
                break;
              }
            }
          } else {
            const fetchedSortedContracts = {
              userId: currentActiveUser.userId,
              contracts: sortedContracts,
              endPoint: endpoint,
            };
            allContracts.push(fetchedSortedContracts);
          }
          setAllContracts(allContracts);
        }
      } else if (currentActiveUser.userId === 0) {
        setUserHasNoContracts(true);
      }
      setContractsFetched(true);
    }
    // Execute the created function directly
    if (endpoint !== 'anon' && currentActiveUser.userId !== 0) {
      fetchUserContracts();
    }
  }, [currentActiveUser, endpoint, userHasNoContracts, contractIndex]);

  React.useEffect(() => {
    async function fetchBasicData(): Promise<void> {
      if (!loadingBasicData && currentActiveUser.userId && currentActiveUser.userId !== 0) {
        dispatch(['changeLoadingBasicData', true]);
        dispatchConsumption(['changeDataLoading', true]);
        const fetchedBasicData = await fetchContractBasicData(currentActiveUser.userId);

        if (fetchedBasicData === null) {
          dispatch(['changeMaintenance', { active: true, message: t('landingPage:login.maintenanceError') }]);
        } else {
          dispatch(['changeBasicData', fetchedBasicData]);
          dispatch(['changeLoadingBasicData', false]);
        }

        if (fetchedBasicData && fetchedBasicData.length > 0) {
          const sortedData = fetchedBasicData.sort(
            (a, b) => a.contractTypeNo.localeCompare(b.contractTypeNo) || a.contractStatus.localeCompare(b.contractStatus),
          );

          const firstActiveMeteringPoint = sortedData.find((mp) => mp.contractStatus === 'A') || sortedData[0];

          const defaultMeteringPoint = {
            customerId: parseInt(firstActiveMeteringPoint.customerNo),
            contractCode: firstActiveMeteringPoint.contractCode,
            contractNo: parseInt(firstActiveMeteringPoint.contractNo),
            meteringPointId: firstActiveMeteringPoint.GSRN,
          };

          // While loading basicData selectedMeteringPoint might have reset to undefined, change it to default or previous one just to be sure

          if (
            !selectedMeteringPoint ||
            !sortedData.find(
              (mp) => mp.GSRN === selectedMeteringPoint.meteringPointId && parseInt(mp.contractNo) === selectedMeteringPoint.contractNo,
            )
          ) {
            dispatch(['changeSelectedMeteringPoint', defaultMeteringPoint]);
          } else {
            dispatch(['changeSelectedMeteringPoint', selectedMeteringPoint]);
          }
        } else {
          dispatchConsumption(['changeDataLoading', false]);
        }
      } else {
        if (basicData !== undefined) {
          dispatch(['changeBasicData', undefined]);
          dispatch(['changeLoadingBasicData', false]);
        }
        dispatchConsumption(['changeDataLoading', false]);
      }
    }

    fetchBasicData();
  }, [currentActiveUser]);

  React.useEffect(() => {
    async function fetchUserLedgers(): Promise<void> {
      const userId = users.activeUser.userId;

      if (userId !== 0) {
        const fetchedLedgers = await fetchLedgers(userId, users.activeUser.userId);

        if (fetchedLedgers) {
          dispatch(['changeLedgers', fetchedLedgers]);
        }
        if (fetchedLedgers && fetchedLedgers.length === 0 && contracts && contracts?.length > 0) {
          // If no ledgers are found, we fetch the ledgers for the first contract
          const fetchedLedgersContractOwner = await fetchLedgers(contracts[0].customerId, users.activeUser.userId);
          if (fetchedLedgersContractOwner) {
            dispatch(['changeLedgers', fetchedLedgersContractOwner]);
          }
        }
      }
    }
    fetchUserLedgers();
  }, [selectedMeteringPoint]);

  React.useEffect(() => {
    let unMounted = false;

    // Create an scoped async function in the hook
    async function fetchUserInvoices(): Promise<void> {
      setFetchingInvoices(true);
      let invoices: InvoiceAkamon[] = [];
      let nextPageToken = '';
      if (!unMounted) {
        const invoicesQuery = await fetchInvoices('customer', currentActiveUser.userId, currentActiveUser.userId);
        invoices = invoicesQuery.invoices;
        nextPageToken = invoicesQuery.nextPageToken;
      }

      if (invoices) {
        invoices.sort((a, b) => (a.dueDate < b.dueDate ? 1 : -1));
        dispatch(['changeInvoices', invoices]);
        dispatch(['changeInvoicesNextPageToken', nextPageToken]);
      }
      setFetchingInvoices(false);
    }
    // Execute the created function directly

    if (!fetchingInvoices && endpoint !== 'anon') {
      fetchUserInvoices();
    }

    return (): void => {
      unMounted = true;
    };
  }, [currentActiveUser, endpoint]);

  React.useEffect(() => {
    let unMounted = false;

    // Check if the user has additional contracts and fetch invoices for those

    async function fetchAdditionalInvoices(): Promise<void> {
      const contractsObject = allContracts.find((co) => co.userId === currentActiveUser.userId);
      if (contractsObject) {
        const otherContracts = contractsObject.contracts.filter((c) => c.customerId !== parseInt(currentActiveUser.userId.toString()));

        if (!unMounted && otherContracts && additionalInvoices.length === 0) {
          const promises: Promise<InvoiceAkamonPaginated>[] = [];
          otherContracts.forEach((contract) => {
            promises.push(fetchInvoices('contract', contract.customerId, currentActiveUser.userId, contract.contractCode));
          });

          Promise.all(promises).then((arrOfResults) => {
            const results: InvoiceAkamon[] = [];
            arrOfResults.forEach((object: InvoiceAkamonPaginated) => {
              object.invoices.forEach((invoice: InvoiceAkamon) => {
                if (invoice.dueDate && !results.find((inv) => inv.identifier === invoice.identifier)) {
                  results.push(invoice);
                }
              });
              if (results.length > 0) {
                setAdditionalInvoices(results);
              }
            });
          });
        } else {
          setAdditionalInvoices([]);
        }
      }
    }

    if (invoices !== undefined && additionalInvoices.length === 0 && contracts && contracts[0]) {
      fetchAdditionalInvoices();
    }

    return (): void => {
      unMounted = true;
    };
  }, [invoices, contracts, additionalInvoices]);

  // Merge personal invoices and additional invoices to one array

  React.useEffect(() => {
    let unMounted = false;

    if (additionalInvoices.length > 0) {
      let extraInvoices: InvoiceAkamon[] = [];
      let mergedInvoices = invoices;
      if (additionalInvoices.length > 0) {
        extraInvoices = additionalInvoices
          .filter((ai) => !invoices?.find((i) => i.identifier === ai.identifier))
          .map((invoice) => {
            // All dates have a time of 00:00:00 so we remove that from what we will show the end user.
            const dueDate = invoice.dueDate.split('T')[0];
            const invoiceDate = invoice.dateOfInvoice.split('T')[0];
            invoice.dueDate = dueDate;
            invoice.dateOfInvoice = invoiceDate;

            return invoice;
          });
      } else {
        extraInvoices = [];
      }

      if (mergedInvoices) {
        mergedInvoices = mergedInvoices.concat(extraInvoices);
      } else {
        mergedInvoices = extraInvoices;
      }

      if (mergedInvoices && !unMounted) {
        mergedInvoices.sort((a, b) => (a.dueDate < b.dueDate ? 1 : -1));
        dispatch(['changeInvoices', mergedInvoices]);
      }
    }

    return (): void => {
      unMounted = true;
    };
  }, [additionalInvoices]);

  React.useEffect(() => {
    if (contractsFetched && userHasNoContracts !== currentActiveUser.userId) {
      let userFound = false;

      for (const contractIndex in allContracts) {
        if (allContracts[contractIndex].userId === currentActiveUser.userId && allContracts[contractIndex].endPoint === currentActiveUser.endpoint) {
          if (allContracts[contractIndex].contracts.length > 0) {
            userFound = true;

            dispatch(['changeContracts', allContracts[contractIndex].contracts]);
          } else {
            allContracts.splice(parseInt(contractIndex));
          }
        }
      }
      if (!userFound) {
        if (currentActiveUser.userId !== 0) {
          setContractsFetched(false);
        }
      }
    }
  }, [currentActiveUser, contracts, contractsFetched, selectedMeteringPoint, userHasNoContracts]);

  const location = useLocation();

  React.useEffect(() => {
    if (basicData && basicData.length > 0 && selectedMeteringPoint && selectedMeteringPoint.contractCode === '') {
      const defaultMeteringPoint = {
        customerId: parseInt(basicData[0].customerNo),
        contractCode: basicData[0].contractCode,
        contractNo: parseInt(basicData[0].contractNo),
        meteringPointId: basicData[0].GSRN,
      };

      dispatch(['changeSelectedMeteringPoint', defaultMeteringPoint]);
    }
  }, [location.pathname]);

  return children;
};

export default ContractsWrapper;
